import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import DateRangePicker from '@Components/DateRangePicker';
import FilterSelect from '@Components/Filter/Dropdown';
import FilterSearch from '@Components/Filter/Search';

import { ShipmentObj } from '@Reducers/Invoice/Type';

import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { AccountHolderUnbilledData } from '../../Type';
import {
  CreditDebitNoteType,
  CreditDebitSearchFilterList,
  MappedCreditDebitShipmentRow,
  SelectedCreditDebitParamType,
  getCreditDebitGroupKey,
  isSameCreditDebitEntry,
  mapCreditDebitShipmentsToGridRows,
} from '../utils/creditDebitNoteUtils';

const EMPTY_SHIPMENTS: ShipmentObj[] = [];

interface TextWithTooltipProps {
  text: string | number | null | undefined;
  title?: string;
  sx?: object;
  className?: string;
  component?: React.ElementType;
}

const TextWithTooltip = React.memo(function TextWithTooltip({
  text,
  title,
  sx = {},
  className = '',
  component = 'span',
}: TextWithTooltipProps) {
  const displayString = text !== null && text !== undefined && text !== '' ? String(text) : '-';
  const tooltipContent = title || (text ? String(text) : '');

  if (!tooltipContent || tooltipContent === '-') {
    return (
      <Typography
        component={component}
        className={className}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          ...sx,
        }}
      >
        {displayString}
      </Typography>
    );
  }

  return (
    <Tooltip title={tooltipContent} arrow enterDelay={250} placement="top">
      <Typography
        component={component}
        className={className}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          cursor: 'inherit',
          ...sx,
        }}
      >
        {displayString}
      </Typography>
    </Tooltip>
  );
});

interface ShipmentRowCardProps {
  shipment: ShipmentObj;
  mappedRow: MappedCreditDebitShipmentRow;
  isChecked: boolean;
  onToggle: (shipment: ShipmentObj, checked: boolean) => void;
  currencySymbol: string;
}

const ShipmentRowCard = React.memo(function ShipmentRowCard({
  shipment,
  mappedRow,
  isChecked,
  onToggle,
  currencySymbol,
}: ShipmentRowCardProps) {
  const theme = useTheme();

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onToggle(shipment, e.target.checked);
    },
    [onToggle, shipment],
  );

  const routeSummary = `${mappedRow.shipper?.city || '-'} → ${mappedRow.consignee?.city || '-'}`;
  const shipperFull = `${mappedRow.shipper?.name || '-'} (${mappedRow.shipper?.city || '-'}, ${
    mappedRow.shipper?.country || '-'
  })`;
  const consigneeFull = `${mappedRow.consignee?.name || '-'} (${mappedRow.consignee?.city || '-'}, ${
    mappedRow.consignee?.country || '-'
  })`;

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: '8px',
        border: `1px solid ${isChecked ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
        bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.02) : 'background.paper',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.04)}`,
        },
      }}
    >
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={1} sm={0.7} md={0.6}>
          <Checkbox
            size="small"
            checked={isChecked}
            onChange={handleCheckboxChange}
            sx={{ p: 0.5 }}
            inputProps={{ 'aria-label': `Select shipment ${mappedRow.shipmentNo}` }}
          />
        </Grid>

        <Grid item xs={11} sm={3.3} md={3.2}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Link
                to={`/shipment/admin/edit/${mappedRow.shipmentNo}`}
                className="sb-text-black underline"
                style={{
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
                title={`View shipment ${mappedRow.shipmentNo}`}
              >
                {mappedRow.shipmentNo}
              </Link>
              {mappedRow.awb && mappedRow.awb !== '-' && (
                <Chip
                  size="small"
                  label={mappedRow.awb}
                  sx={{ height: 18, fontSize: '0.68rem', bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', flexShrink: 0 }}>
                {mappedRow.date}
              </Typography>
              {mappedRow.serviceName && mappedRow.serviceName !== '-' && (
                <TextWithTooltip
                  text={`• ${mappedRow.serviceName}`}
                  title={`Service: ${mappedRow.serviceName}`}
                  sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
                />
              )}
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={6} sm={2.6} md={2.4}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              <PlaceOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
              <TextWithTooltip
                text={routeSummary}
                title={`${shipperFull} → ${consigneeFull}`}
                sx={{ fontSize: '0.76rem', fontWeight: 600, color: 'text.primary' }}
              />
            </Stack>
            <TextWithTooltip
              text={`To: ${mappedRow.consignee?.name || '-'}`}
              title={consigneeFull}
              sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
            />
          </Stack>
        </Grid>

        <Grid item xs={6} sm={1.8} md={1.8}>
          <Chip
            size="small"
            label={mappedRow.status}
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 600,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'text.primary',
            }}
          />
        </Grid>

        <Grid item xs={6} sm={1.6} md={1.8}>
          <Stack spacing={0.2} sx={{ minWidth: 0 }}>
            <TextWithTooltip
              text={mappedRow.weight}
              sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}
            />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {mappedRow.packages} {mappedRow.packages === 1 ? 'pkg' : 'pkgs'}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={6} sm={2} md={2.2} sx={{ textAlign: 'right' }}>
          <TextWithTooltip
            text={`${currencySymbol} ${mappedRow.totalAmount}`}
            title={`Amount: ${currencySymbol} ${mappedRow.totalAmount}`}
            sx={{
              fontSize: '0.86rem',
              fontWeight: 700,
              color: 'primary.main',
              display: 'inline-block',
              maxWidth: '100%',
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
});

interface InvoiceRowCardProps {
  data: AccountHolderUnbilledData;
  noteType: CreditDebitNoteType;
  selectedAccountRows: SelectedCreditDebitParamType[];
  isExpanded: boolean;
  onToggleExpand: (groupKey: string) => void;
  onSelectInvoiceGroup: (data: AccountHolderUnbilledData, checked: boolean) => void;
  onToggleSingleShipment: (data: AccountHolderUnbilledData, shipment: ShipmentObj, checked: boolean) => void;
  currencySymbol: string;
}

const InvoiceRowCard = React.memo(function InvoiceRowCard({
  data,
  noteType,
  selectedAccountRows,
  isExpanded,
  onToggleExpand,
  onSelectInvoiceGroup,
  onToggleSingleShipment,
  currencySymbol,
}: InvoiceRowCardProps) {
  const theme = useTheme();
  const locale = useLocale();

  const groupKey = useMemo(() => getCreditDebitGroupKey(data), [data]);
  const shipments = data.shipments || EMPTY_SHIPMENTS;

  const currentSelection = useMemo(
    () => selectedAccountRows.find(item => isSameCreditDebitEntry(item, data)),
    [selectedAccountRows, data],
  );

  const selectedCount = currentSelection?.shipments?.length || 0;
  const isAllSelected = shipments.length > 0 && selectedCount === shipments.length;
  const isPartiallySelected = selectedCount > 0 && selectedCount < shipments.length;

  const mappedShipmentRows = useMemo(
    () => mapCreditDebitShipmentsToGridRows(shipments, noteType, locale),
    [shipments, noteType, locale],
  );

  const customerName = data.company ? data.company.name : data.user ? data.user.name : '-';
  const customerEmail = data.company ? data.company.email : data.user ? data.user.email : '-';
  const systemName = data.system?.name || (typeof data._systemId === 'object' ? data._systemId?.name : '') || '-';
  const invoiceNumber = data.invoiceNumber || '-';
  const totalInvoiceAmount = data.totalInvoiceAmount || 0;

  const handleInvoiceCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onSelectInvoiceGroup(data, e.target.checked);
    },
    [data, onSelectInvoiceGroup],
  );

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleToggleRowExpand = useCallback(() => {
    onToggleExpand(groupKey);
  }, [onToggleExpand, groupKey]);

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: `1px solid ${selectedCount > 0 ? alpha(theme.palette.primary.main, 0.5) : theme.palette.divider}`,
        borderLeft:
          selectedCount > 0 ? `4px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
        bgcolor: selectedCount > 0 ? alpha(theme.palette.primary.main, 0.015) : 'background.paper',
        transition: 'all 0.18s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.45),
        },
      }}
    >
      {/* Collapsed Invoice Header */}
      <Box
        onClick={handleToggleRowExpand}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '38px 1fr auto',
            md: '38px minmax(140px, 1.2fr) minmax(130px, 1.1fr) minmax(110px, 1fr) minmax(120px, 1.1fr) auto',
          },
          alignItems: 'center',
          gap: { xs: 1, md: 1.5 },
          p: { xs: 1.25, sm: 1.5 },
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box onClick={handleStopPropagation}>
          <Checkbox
            size="small"
            checked={isAllSelected}
            indeterminate={isPartiallySelected}
            onChange={handleInvoiceCheckboxChange}
            sx={{ p: 0.5 }}
            inputProps={{ 'aria-label': `Select invoice ${invoiceNumber}` }}
          />
        </Box>

        <Stack spacing={0.2} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ReceiptOutlinedIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
            <TextWithTooltip
              text={`Inv #${invoiceNumber}`}
              title={`Invoice Number: ${invoiceNumber}`}
              sx={{ fontWeight: 700, fontSize: '0.86rem', color: 'text.primary' }}
            />
          </Stack>
          <TextWithTooltip
            text={`Account: ${data.account?.accountNumber || data.accountNumber || '-'}`}
            title={`Account Number: ${data.account?.accountNumber || data.accountNumber || '-'}`}
            sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
          />
        </Stack>

        <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
          <TextWithTooltip
            text={customerName}
            title={customerName}
            sx={{ fontWeight: 600, fontSize: '0.82rem', color: 'text.primary' }}
          />
          <TextWithTooltip
            text={customerEmail}
            title={customerEmail}
            sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
          />
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
          <TextWithTooltip
            text={systemName}
            title={`System: ${systemName}`}
            sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
          />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{shipments.length} shipment(s)</Typography>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right', minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>Invoice Amount</Typography>
          <TextWithTooltip
            text={`${currencySymbol} ${formatNumber(totalInvoiceAmount, locale)}`}
            title={`Total: ${currencySymbol} ${formatNumber(totalInvoiceAmount, locale)}`}
            sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'text.primary' }}
          />
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          {selectedCount > 0 && (
            <Chip
              size="small"
              color="primary"
              label={`${selectedCount}/${shipments.length} selected`}
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
            />
          )}
          <IconButton
            size="small"
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              p: 0.5,
            }}
            aria-label={isExpanded ? 'Collapse invoice shipments' : 'Expand invoice shipments'}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Expanded Shipments Drawer */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: { xs: 1.25, sm: 1.75 }, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
                Shipments in Invoice #{invoiceNumber} ({shipments.length})
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              Check shipments to include in this note
            </Typography>
          </Stack>

          <Stack spacing={0.75}>
            {mappedShipmentRows.map((mappedRow, idx) => {
              const shipment = shipments[idx];
              const isChecked = Boolean(currentSelection?.shipments?.some(s => s._id === shipment._id));
              return (
                <ShipmentRowCard
                  key={shipment?._id || idx}
                  shipment={shipment}
                  mappedRow={mappedRow}
                  isChecked={isChecked}
                  onToggle={(s, chk) => onToggleSingleShipment(data, s, chk)}
                  currencySymbol={currencySymbol}
                />
              );
            })}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
});

export interface CreditDebitShipmentSectionProps {
  noteType: CreditDebitNoteType;
  list: AccountHolderUnbilledData[];
  totalCount: number;
  currentPage: number;
  perPage: number;
  loading: boolean;
  activeSystemList: { display: string; value: string }[];
  searchFilterList?: { label: string; value: string }[];
  filterSelectValue: string;
  filterText: string;
  dateRange: { startDate: Date; endDate: Date };
  system: string[];
  selectedAccountRows: SelectedCreditDebitParamType[];
  expandedInvoices: Record<string, boolean>;
  primaryCurrencySymbol: string;
  onSearchSelectChange: (val: string) => void;
  onSearchTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchTextSubmit: () => void;
  onTextClear: () => void;
  onDateChange: (range: { startDate: Date; endDate: Date }, isOpen: boolean) => void;
  onSystemFilter: (sys: string[]) => void;
  onClearFilters: () => void;
  onPageChange: (e: unknown, newPage: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectAllInvoices: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectInvoiceGroup: (data: AccountHolderUnbilledData, checked: boolean) => void;
  onToggleSingleShipment: (data: AccountHolderUnbilledData, shipment: ShipmentObj, checked: boolean) => void;
  onToggleExpandInvoice: (groupKey: string) => void;
}

const CreditDebitShipmentSection = React.memo(function CreditDebitShipmentSection(
  props: CreditDebitShipmentSectionProps,
) {
  const {
    noteType,
    list,
    totalCount,
    currentPage,
    perPage,
    loading,
    activeSystemList,
    searchFilterList,
    filterSelectValue,
    filterText,
    dateRange,
    system,
    selectedAccountRows,
    expandedInvoices,
    primaryCurrencySymbol,
    onSearchSelectChange,
    onSearchTextChange,
    onSearchTextSubmit,
    onTextClear,
    onDateChange,
    onSystemFilter,
    onClearFilters,
    onPageChange,
    onRowsPerPageChange,
    onSelectAllInvoices,
    onSelectInvoiceGroup,
    onToggleSingleShipment,
    onToggleExpandInvoice,
  } = props;

  const theme = useTheme();
  const titleText = noteType === 'credit' ? 'Credit Note' : 'Debit Note';

  const isAllInvoicesSelected = useMemo(() => {
    if (!list?.length) return false;
    return list.every(item => {
      const match = selectedAccountRows.find(row => isSameCreditDebitEntry(row, item));
      return match && match.shipments.length === (item.shipments?.length || 0) && match.shipments.length > 0;
    });
  }, [list, selectedAccountRows]);

  const isSomeInvoicesSelected = useMemo(() => {
    if (!list?.length || isAllInvoicesSelected) return false;
    return selectedAccountRows.some(row => (row.shipments?.length || 0) > 0);
  }, [list, isAllInvoicesSelected, selectedAccountRows]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top Section Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '7px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'text.primary', lineHeight: 1.2 }}>
              Unbilled {titleText}s & Shipments
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
              Select invoices or expand rows to choose specific shipments
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Compact Horizontally Scrollable Filter Toolbar with ultra-thin 2.5px scrollbar */}
      <Box
        sx={{
          p: '6px 10px',
          mb: 1,
          borderRadius: '10px',
          bgcolor: alpha(theme.palette.primary.main, 0.025),
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.18)} transparent`,
          '&::-webkit-scrollbar': {
            height: '2.5px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.18),
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.28),
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.45),
          },
        }}
      >
        <Box sx={{ width: '270px', minWidth: '240px', flexShrink: 0 }}>
          <FilterSearch
            onSelectChange={onSearchSelectChange}
            list={searchFilterList || CreditDebitSearchFilterList}
            selectValue={filterSelectValue}
            textValue={filterText}
            onTextChange={onSearchTextChange}
            onTextSearch={onSearchTextSubmit}
            onTextClear={onTextClear}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <DateRangePicker onChange={onDateChange} value={dateRange} />
        </Box>
        <Box sx={{ width: '150px', minWidth: '130px', flexShrink: 0 }}>
          <FilterSelect
            id="cd-modal-system"
            label="System"
            value={system}
            options={activeSystemList}
            onChange={onSystemFilter}
          />
        </Box>
        <SbButton
          className="xs-text"
          variant="outlined"
          startIcon={<FilterAltOffOutlinedIcon fontSize="small" />}
          sx={{
            borderRadius: '8px !important',
            padding: '5px 12px !important',
            height: '36px !important',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          onClick={onClearFilters}
        >
          Reset
        </SbButton>
      </Box>

      {/* Master Selection Sub-header Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 1.25, sm: 2 },
          py: 0.5,
          mb: 1,
          borderRadius: '8px',
          bgcolor: alpha(theme.palette.text.primary, 0.03),
          border: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            size="small"
            checked={isAllInvoicesSelected}
            indeterminate={isSomeInvoicesSelected}
            onChange={onSelectAllInvoices}
            sx={{ p: 0.4 }}
            inputProps={{ 'aria-label': 'Select all unbilled invoices on current page' }}
          />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
            Select All on Page
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', fontWeight: 500 }}>
          Showing {list.length} of {totalCount || list.length} unbilled {titleText.toLowerCase()}(s)
        </Typography>
      </Box>

      {/* Scrollable Invoice List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {list.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              bgcolor: 'background.paper',
              borderRadius: '12px',
              border: `1px dashed ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'text.secondary' }}>
              {loading
                ? `Loading unbilled ${titleText.toLowerCase()}s...`
                : `No unbilled ${titleText.toLowerCase()}s found`}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.5, maxWidth: 360 }}>
              {loading
                ? 'Please wait while records are being fetched.'
                : 'Try adjusting the date range, search query, or system filter above.'}
            </Typography>
          </Box>
        ) : (
          list.map((item, idx) => {
            const key = getCreditDebitGroupKey(item) || String(idx);
            return (
              <InvoiceRowCard
                key={key}
                data={item}
                noteType={noteType}
                selectedAccountRows={selectedAccountRows}
                isExpanded={Boolean(expandedInvoices[key])}
                onToggleExpand={onToggleExpandInvoice}
                onSelectInvoiceGroup={onSelectInvoiceGroup}
                onToggleSingleShipment={onToggleSingleShipment}
                currencySymbol={primaryCurrencySymbol}
              />
            );
          })
        )}
      </Box>

      {/* Pagination Bar */}
      <Box
        sx={{
          pt: 0.75,
          mt: 0.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <TablePagination
          component="div"
          count={totalCount || list.length}
          page={currentPage - 1}
          rowsPerPage={perPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': { minHeight: 40, p: 0 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.78rem',
              m: 0,
            },
          }}
        />
      </Box>
    </Box>
  );
});

export default CreditDebitShipmentSection;
